import { Suspense, type ReactNode } from "react";
import { useTranslate } from "ra-core";
import { ErrorBoundary } from "react-error-boundary";
import { ExternalLink } from "lucide-react";
import { Link, useLocation } from "react-router";
import { Notification } from "@/components/admin/notification";
import { Error } from "@/components/admin/error";
import { UserMenu } from "@/components/admin/user-menu";
import { Skeleton } from "@/components/ui/skeleton";
import { cn } from "@/lib/utils";

import { useConfigurationLoader } from "../root/useConfigurationLoader";
import { AppMark } from "../palmtree/shell";
import { DesktopGlobalSearch } from "../search/GlobalSearch";
import { ProfileMenu } from "./ProfileMenu";
import {
  externalNavAriaLabel,
  isPrimaryNavActive,
  PRIMARY_NAV,
} from "./primaryNav";

export const Layout = ({ children }: { children: ReactNode }) => {
  useConfigurationLoader();
  return (
    <div className="flex min-h-svh bg-background">
      <PalmTreeNav />
      <div className="flex min-w-0 flex-1 flex-col">
        <PalmTreeHeader />
        <main className="flex-1 px-7 py-7" id="main-content">
          <ErrorBoundary FallbackComponent={Error}>
            <Suspense
              fallback={<Skeleton className="h-12 w-12 rounded-full" />}
            >
              {children}
            </Suspense>
          </ErrorBoundary>
        </main>
      </div>
      <Notification />
    </div>
  );
};

export const PalmTreeNav = () => {
  const location = useLocation();
  const translate = useTranslate();
  return (
    <div className="hidden md:flex w-28 shrink-0 flex-col items-center overflow-visible bg-nav py-4 gap-4">
      <AppMark />
      <DesktopGlobalSearch />
      <nav
        className="flex flex-col items-center gap-4"
        aria-label={translate("palmtree.nav.main")}
      >
        {PRIMARY_NAV.map((item) => {
          const active = isPrimaryNavActive(item, location.pathname);
          const Icon = item.icon;
          const label = translate(item.labelKey);
          const className =
            "flex w-24 flex-col items-center gap-1 px-1 text-center text-[11px] leading-tight";
          const content = (
            <>
              <span
                className={cn(
                  "flex size-10 items-center justify-center rounded-row",
                  active ? "bg-card shadow-card" : undefined,
                )}
              >
                <Icon
                  className={cn(
                    "size-5",
                    active ? "text-primary" : "text-muted-foreground",
                  )}
                  strokeWidth={1.75}
                />
              </span>
              <span
                className={cn(
                  "inline-flex items-center gap-0.5",
                  active
                    ? "font-[650] text-foreground"
                    : "font-medium text-muted-foreground",
                )}
              >
                {label}
                {item.external ? (
                  <ExternalLink aria-hidden="true" className="size-3" />
                ) : null}
              </span>
            </>
          );
          if (item.external) {
            return (
              <a
                key={item.to}
                href={item.to}
                target="_blank"
                rel="noopener noreferrer"
                aria-label={externalNavAriaLabel(label, translate)}
                className={className}
              >
                {content}
              </a>
            );
          }
          return (
            <Link
              key={item.to}
              to={item.to}
              aria-current={active ? "page" : undefined}
              className={className}
            >
              {content}
            </Link>
          );
        })}
      </nav>
    </div>
  );
};

const PalmTreeHeader = () => (
  <header className="flex h-16 items-center gap-4 border-b border-border bg-background px-7">
    <h1 className="font-heading text-base font-extrabold">PalmTree</h1>
    <div className="ml-auto flex items-center gap-2">
      <UserMenu>
        <ProfileMenu />
      </UserMenu>
    </div>
  </header>
);
